import { FocusMonitor } from '@angular/cdk/a11y'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { Component, ElementRef, Input, OnDestroy, OnInit, Optional, Self } from '@angular/core'
import { FormGroupDirective, NgControl, NgForm, UntypedFormControl } from '@angular/forms'
import { MatFormFieldControl } from '@angular/material/form-field'
import { CustomMaterialInputControlComponent } from '@mmx/components'
import { ReferrerModel, ReferrersService } from '@mmx/shared'
import { first } from 'lodash'
import { ReplaySubject, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

@Component({
  selector: 'mmx-referrer-selector-input',
  template: ` <div fxFlex fxLayout="row" class="mat-form-field">
    <mat-select [formControl]="referrerFormControl" [multiple]="isMultiple">
      <mat-option *ngIf="referrers.length > 1">
        <ngx-mat-select-search
          [formControl]="referrerFilterCtrl"
          [placeholderLabel]="'referrer-selector.search-referrers' | translate"
          [noEntriesFoundLabel]="
            'referrer-selector.no-matching-referrers' | translate
          "
        ></ngx-mat-select-search>
      </mat-option>
      <mmx-select-all-option
        *ngIf="isMultiple"
        [control]="referrerFormControl"
        [values]="referrerIds"
      ></mmx-select-all-option>
      <mat-option *ngIf="!required" [value]="null">{{ 'common.none' | translate }}</mat-option>
      <mat-option
        *ngFor="let referrer of filteredReferrers | async"
        [disabled]="doesRequireNPI ? !referrer.npi : false"
        [value]="isFull ? referrer : referrer.id"
      >
      {{ referrer.viewValue }}
      </mat-option>
    </mat-select>
  </div>`,
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: ReferrerSelectorFieldComponent,
    },
  ],
  standalone: false,
})
export class ReferrerSelectorFieldComponent
  extends CustomMaterialInputControlComponent<string[]>
  implements OnInit, OnDestroy
{
  referrerFormControl: UntypedFormControl = new UntypedFormControl()
  referrers: ReferrerModel[] = []
  referrerIds: string[] = []
  filteredReferrers: ReplaySubject<ReferrerModel[]> = new ReplaySubject<ReferrerModel[]>(
    1,
  )
  referrerFilterCtrl: UntypedFormControl = new UntypedFormControl()
  @Input() fullResponse = false
  @Input() multiple = false
  @Input() selectAll = true
  @Input() requireNPI = false

  @Input()
  get value(): string[] | null {
    return this._value
  }
  set value(referrers: string[] | null) {
    this._value = referrers
    this.updateControlValue()
    this.stateChanges.next()
    if (this.onChange) {
      this.onChange(this.value)
    }
  }

  get isMultiple(): boolean {
    return coerceBooleanProperty(this.multiple)
  }

  get isFull(): boolean {
    return coerceBooleanProperty(this.fullResponse)
  }

  get doesRequireNPI(): boolean {
    return coerceBooleanProperty(this.requireNPI)
  }

  protected _placeholder = 'common.referrer'
  protected _value: string[]

  private onDestroy$ = new Subject<void>()

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() protected parentForm: NgForm,
    @Optional() protected parentFormGroup: FormGroupDirective,
    protected fm: FocusMonitor,
    protected elRef: ElementRef,
    private referrersService: ReferrersService,
  ) {
    super(ngControl, parentForm, parentFormGroup, fm, elRef)
  }

  ngOnInit() {
    super.ngOnInit()

    this.referrersService.referrers$.pipe(takeUntil(this.onDestroy$)).subscribe(referrers => {
      this.referrers = referrers
      this.referrerIds = this.referrers.map((f) => f.id)

      // if the value was a referrerId, transform it into a referrer now that we loaded the referrers
      if (this._value != null) {
        this.writeValue(this._value)
      }

      this.filteredReferrers.next(this.referrers.slice())

      this.referrerFilterCtrl.valueChanges
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          this.filterReferrer()
        })

      this.referrerFormControl.valueChanges.subscribe((value) => {
        this._value = value
        this.stateChanges.next()
        if (this.onChange) {
          this.onChange(this.value)
        }
      })

      if (!this.value && this.referrers.length === 1 && !this.isMultiple) {
        this.writeValue(first(this.referrers).id)
      } else if (this.value != null) {
        this.updateControlValue()
      }
    })
  }

  ngOnDestroy(): void {
    super.ngOnDestroy()
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  writeValue(value: string | string[]) {
    if (typeof value === 'string') {
      this.value = [value]
    } else if (value instanceof ReferrerModel) {
      this.value = [value.id]
    } else {
      this.value = value
    }
  }

  private updateControlValue() {
    if (this.isMultiple) {
      this.referrerFormControl.setValue(this._value)
    } else if (this._value != null && this._value.length > 0) {
      this.referrerFormControl.setValue(this._value[0])
    }

    this.referrerFormControl.updateValueAndValidity()
  }

  private filterReferrer() {
    if (!this.referrers) {
      return
    }
    // get the search keyword
    let search = this.referrerFilterCtrl.value
    if (!search) {
      this.filteredReferrers.next(this.referrers.slice())
      return
    } else {
      search = search.toLowerCase()
    }
    // filter the facilities
    this.filteredReferrers.next(
      this.referrers.filter(
        (referrer) => referrer.shortName.toLowerCase().indexOf(search) > -1,
      ),
    )
  }
}
